import { io, Socket } from "socket.io-client";

export const ADMIN_APPOINTMENT_SOCKET_EVENT =
  "admin-appointment-socket-message";

type AppointmentSocketAppointment = {
  id?: string;
  clientName?: string;
  date?: string;
  time?: string;
  start_at?: string;
  end_at?: string;
  unit_id?: string;
  unitName?: string;
  unit_name?: string;
  service_id?: string;
  serviceName?: string;
  endTime?: string;
  end_time?: string;
  endTimeFormatted?: string;
  end_time_formatted?: string;
  startTime?: string;
  start_time?: string;
  time_start?: string;
  time_end?: string;
  timeRange?: string;
  time_range?: string;
  timeRangeFormatted?: string;
  time_range_formatted?: string;
  organizationId?: string;
  [key: string]: any;
};

export type AdminAppointmentSocketPayload = {
  type:
    | "APPOINTMENT_CREATED"
    | "APPOINTMENT_CANCELLED"
    | "APPOINTMENT_UPDATED"
    | string;
  message?: string;
  appointment?: AppointmentSocketAppointment;
  previousAppointment?: AppointmentSocketAppointment;
  timestamp?: string;
  [key: string]: any;
};

type SocketEventPayload = AdminAppointmentSocketPayload | string | null | undefined;

type AdminAppointmentSocketOptions = {
  apiUrl: string;
  organizationId: string;
  isNotificationPlaybackAllowed?: () => boolean;
  onAppointmentCreated: (payload: AdminAppointmentSocketPayload) => void;
  onAppointmentCancelled: (payload: AdminAppointmentSocketPayload) => void;
  onAppointmentUpdated: (payload: AdminAppointmentSocketPayload) => void;
};

let socket: Socket | null = null;
let currentOptions: AdminAppointmentSocketOptions | null = null;
let currentConnectionKey = "";
let notificationSound: HTMLAudioElement | null = null;

const APPOINTMENT_EVENT_TYPES = new Set([
  "APPOINTMENT_CREATED",
  "APPOINTMENT_CANCELLED",
  "APPOINTMENT_UPDATED",
]);

const getNotificationSound = () => {
  if (typeof Audio === "undefined") return null;
  if (!notificationSound) {
    notificationSound = new Audio("/sounds/notification.mp3");
    notificationSound.preload = "auto";
  }
  return notificationSound;
};

export function unlockNotificationSound() {
  const sound = getNotificationSound();
  if (!sound) return;

  const previousMuted = sound.muted;
  const previousVolume = sound.volume;
  const restoreSound = () => {
    sound.pause();
    sound.currentTime = 0;
    sound.muted = previousMuted;
    sound.volume = previousVolume;
  };

  sound.muted = true;
  sound.volume = 0;
  sound.currentTime = 0;
  sound
    .play()
    .then(restoreSound)
    .catch(restoreSound);
}

const playNotificationSound = () => {
  const sound = getNotificationSound();
  if (!sound) return;

  try {
    sound.currentTime = 0;
    sound.play().catch(() => {});
  } catch {
    // Browser audio policies can still block playback until a user gesture.
  }
};

const closeCurrentSocketWithoutReconnect = () => {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};

const normalizeBaseUrl = (value: string) => {
  const trimmed = value.trim().replace(/\/+$/, "");

  try {
    const parsed = new URL(trimmed);
    const protocol =
      parsed.protocol === "https:" || parsed.protocol === "wss:"
        ? "https:"
        : "http:";
    return `${protocol}//${parsed.host}`;
  } catch {
    return trimmed;
  }
};

const normalizeSocketPayload = (
  payload: SocketEventPayload
): AdminAppointmentSocketPayload | null => {
  if (!payload) return null;
  if (typeof payload !== "string") return payload;

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

export const resolveAdminAppointmentSocketUrl = (apiBaseUrl?: string) => {
  const envUrl = process.env.REACT_APP_API_URL || "";
  if (envUrl.trim()) return normalizeBaseUrl(envUrl.trim());

  if (apiBaseUrl) {
    try {
      return normalizeBaseUrl(apiBaseUrl);
    } catch {
      // Fall through to the current origin.
    }
  }

  if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "https:" : "http:";
    return `${protocol}//${window.location.host}`;
  }

  return "http://localhost:3001";
};

export function connectAdminAppointmentSocket(
  options: AdminAppointmentSocketOptions
) {
  if (!options.organizationId) return;

  const nextKey = `${normalizeBaseUrl(options.apiUrl)}::${
    options.organizationId
  }`;
  currentOptions = options;

  if (
    socket &&
    currentConnectionKey === nextKey &&
    (socket.connected || socket.active)
  ) {
    return;
  }

  closeCurrentSocketWithoutReconnect();
  currentConnectionKey = nextKey;

  const socketBaseUrl = normalizeBaseUrl(options.apiUrl);
  socket = io(socketBaseUrl, {
    path: "/api/socket.io",
    withCredentials: true,
    transports: ["websocket"],
    query: {
      role: "admin",
      organizationId: options.organizationId,
    },
  });

  socket.on("connect", () => {
    console.log("Admin socket connected", {
      id: socket?.id,
      organizationId: options.organizationId,
      url: socketBaseUrl,
    });
  });

  socket.on("message", (rawPayload: SocketEventPayload) => {
    const payload = normalizeSocketPayload(rawPayload);
    if (!payload) return;

    console.log("Admin socket message:", payload);

    if (!APPOINTMENT_EVENT_TYPES.has(payload.type)) {
      console.log("Admin socket ignored message type:", payload.type);
      return;
    }

    if (
      payload.appointment?.organizationId &&
      String(payload.appointment.organizationId) !== String(options.organizationId)
    ) {
      console.warn("Admin socket organization mismatch", {
        connectedOrganizationId: options.organizationId,
        payloadOrganizationId: payload.appointment.organizationId,
      });
    }

    if (currentOptions?.isNotificationPlaybackAllowed?.() === false) {
      disconnectAdminAppointmentSocket();
      return;
    }

    playNotificationSound();

    if (payload.type === "APPOINTMENT_CREATED") {
      currentOptions?.onAppointmentCreated(payload);
    }

    if (payload.type === "APPOINTMENT_CANCELLED") {
      currentOptions?.onAppointmentCancelled(payload);
    }

    if (payload.type === "APPOINTMENT_UPDATED") {
      currentOptions?.onAppointmentUpdated(payload);
    }
  });

  socket.on("disconnect", () => {
    console.log("Admin socket disconnected");
  });

  socket.on("connect_error", (error: any) => {
    console.error("Admin socket connect_error", {
      message: error?.message,
      description: error?.description,
      context: error?.context,
      type: error?.type,
      data: error?.data,
      stack: error?.stack,
      url: socketBaseUrl,
      path: "/api/socket.io",
      organizationId: options.organizationId,
      transport: socket?.io?.engine?.transport?.name,
      error,
    });
  });
}

export function disconnectAdminAppointmentSocket() {
  currentOptions = null;
  currentConnectionKey = "";
  closeCurrentSocketWithoutReconnect();
}
